import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Users, Shield, User, CheckCircle2, XCircle, AlertTriangle, Building2, ArrowRight, LogIn, Crown } from 'lucide-react';
import { apiGet, apiPost, ApiError } from '../lib/apiClient';

const JoinOrg = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => { checkAuthAndFetchInvite(); }, [token]);

  const checkAuthAndFetchInvite = async () => {
    const storedUser = localStorage.getItem('decoded_user');
    const storedToken = localStorage.getItem('decoded_token');

    if (storedUser && storedToken) {
      try {
        const userData = await apiGet('/api/auth/me');
        setUser(userData);
        setIsAuthenticated(true);
      } catch { setIsAuthenticated(false); }
    } else { setIsAuthenticated(false); }

    if (!token) { setError('No invite token provided'); setLoading(false); return; }

    try {
      const data = await apiGet(`/api/org/invite/${token}`, { auth: false });
      setInvite(data);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to load invite details';
      setError(msg || 'Invalid or expired invite');
    }
    finally { setLoading(false); }
  };

  const handleJoin = async () => {
    if (!isAuthenticated) {
      localStorage.setItem('pending_org_invite', token);
      navigate(`/login?redirect=/join-org?token=${token}`);
      return;
    }

    setJoining(true);
    try {
      await apiPost(`/api/org/join/${token}`);
      setSuccess(true);
      toast.success('Successfully joined the organization!');
      try {
        const userData = await apiGet('/api/auth/me');
        localStorage.setItem('decoded_user', JSON.stringify(userData));
      } catch { /* non-fatal */ }
      setTimeout(() => navigate('/organization'), 2000);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to join organization';
      toast.error(msg);
      setError(msg);
    }
    finally { setJoining(false); }
  };

  const getRoleIcon = (role) => {
    if (role === 'admin') return <Shield className="w-5 h-5 text-blue-500" />;
    return <User className="w-5 h-5 text-slate-400" />;
  };

  const getRoleDescription = (role) => {
    if (role === 'admin') return 'You will be able to manage members and have full access to all features.';
    return 'You will be able to use the platform features and view shared data.';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading invite...</p>
        </div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Invalid Invite</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link to="/"><Button variant="outline">Go to Home</Button></Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2" data-testid="join-success-title">Welcome!</h1>
          <p className="text-slate-600 mb-6">
            You've successfully joined <strong>{invite?.org_name}</strong>. Redirecting...
          </p>
          <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2" data-testid="join-org-title">You're Invited!</h1>
          <p className="text-slate-600">
            <strong>{invite?.invited_by_name || 'Someone'}</strong> has invited you to join their organization on Decoded.
          </p>
        </div>

        <div className="bg-slate-50 rounded-xl p-5 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900" data-testid="org-name-display">{invite?.org_name}</h2>
              <p className="text-sm text-slate-500">Organization</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
            {getRoleIcon(invite?.role)}
            <div>
              <p className="font-medium text-slate-900 capitalize">{invite?.role === 'admin' ? 'Admin' : 'Member'} Role</p>
              <p className="text-xs text-slate-500">{getRoleDescription(invite?.role)}</p>
            </div>
          </div>
        </div>

        {isAuthenticated && user?.email?.toLowerCase() !== invite?.email?.toLowerCase() && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Email Mismatch</p>
                <p className="text-sm text-amber-700 mt-1">
                  This invite was sent to <strong>{invite?.email}</strong>, but you're signed in as <strong>{user?.email}</strong>.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {isAuthenticated ? (
            <>
              <Button onClick={handleJoin} disabled={joining || (user?.email?.toLowerCase() !== invite?.email?.toLowerCase())} className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-base" data-testid="join-org-btn">
                {joining ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Joining...</> : <>Join Organization <ArrowRight className="w-5 h-5 ml-2" /></>}
              </Button>
              {user?.email?.toLowerCase() !== invite?.email?.toLowerCase() && (
                <Button variant="outline" onClick={() => { localStorage.removeItem('decoded_user'); localStorage.removeItem('decoded_token'); localStorage.setItem('pending_org_invite', token); navigate(`/login?redirect=/join-org?token=${token}`); }} className="w-full h-12">
                  <LogIn className="w-4 h-4 mr-2" /> Sign in with Different Account
                </Button>
              )}
            </>
          ) : (
            <>
              <Button onClick={() => { localStorage.setItem('pending_org_invite', token); navigate(`/login?redirect=/join-org?token=${token}`); }} className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-base" data-testid="login-to-join-btn">
                <LogIn className="w-4 h-4 mr-2" /> Sign in to Join
              </Button>
              <p className="text-center text-sm text-slate-500">
                Don't have an account?{' '}<Link to={`/signup?redirect=/join-org?token=${token}`} className="text-blue-600 hover:underline font-medium">Sign up</Link>
              </p>
            </>
          )}
        </div>

        <div className="mt-8 pt-6 border-t text-center">
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">Back to Decoded</Link>
        </div>
      </div>
    </div>
  );
};

export default JoinOrg;
